/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import noop from 'lodash/noop';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Button from 'yoda-core-components/lib/components/Button/Button';
import List from 'yoda-core-components/lib/components/List/List';
import NavigationHelper from 'yoda-core-components/lib/helpers/NavigationHelper/NavigationHelper';
import Banner from 'yoda-core-components/lib/components/Banner/Banner';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import IconTriangleDown from 'yoda-core-components/lib/assets/svg/IconTriangleDown.svg';
import { checkUrlExtension, replaceSubdomain } from 'yoda-core-components/lib/helpers/Utils/Utils';
import YodaToolTip from '../YodaTooltip/YodaTooltip';
import Link from '../Link/Link';
import Video from '../VideoPlayer/VideoPlayerLazy';
import * as styles from './EditorialContent.css';
import configuredThemes from './EditorialThemes';
import RichText from '../RichText/RichText';
import HeadingComponent from './HeadingComponent';
import analyticsAction from '../../actions/AnalyticsAction';
import RevieveModal from '../Revieve/RevieveModal';

const cx = classNames.bind(styles);

const getVideoProvider = (provider) => (provider && provider.toUpperCase()) || '';

class EditorialContent extends PureComponent {
    static propTypes = {
        adobeAudience: PropTypes.string,
        automationId: PropTypes.string,
        backgroundColor: PropTypes.string,
        callToActions: PropTypes.array,
        callToActionsGroupLabel: PropTypes.string,
        componentType: PropTypes.string,
        ctaPosition: PropTypes.string,
        deviceType: PropTypes.object,
        headlineAbove: PropTypes.bool,
        imageWidth: PropTypes.string,
        logo: PropTypes.object,
        marketingLabel: PropTypes.string,
        media: PropTypes.object,
        numberOfColumns: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        pageType: PropTypes.string,
        preferences: PropTypes.object,
        primaryLink: PropTypes.object,
        rndKeyString: PropTypes.string,
        style: PropTypes.string,
        theme: PropTypes.object,
        transparent: PropTypes.string,
        actions: PropTypes.object,
        onImageLoad: PropTypes.func,
    };

    static defaultProps = {
        adobeAudience: '',
        automationId: 'editorialcontent',
        backgroundColor: '',
        callToActions: [{}],
        callToActionsGroupLabel: '',
        componentType: '',
        ctaPosition: '',
        deviceType: {},
        headlineAbove: false,
        imageWidth: '',
        logo: {},
        marketingLabel: '',
        media: {},
        numberOfColumns: 4,
        pageType: '',
        preferences: {},
        primaryLink: {},
        rndKeyString: '',
        style: '',
        theme: {},
        transparent: false,
        actions: {},
        onImageLoad: noop,
    };

    constructor(props) {
        super(props);
        this.state = {
            fullExclusionDisplay: false,
            // eslint-disable-next-line react/no-unused-state
            editorialHeroStyleA: false,
            // eslint-disable-next-line react/no-unused-state
            editorialHeroStyleB: false,
            // eslint-disable-next-line react/no-unused-state
            editorialStyle: false,
            controlledIsOpen: false,
            imageError: false,
        };
    }

    componentDidMount() {
        if (!this.props.deviceType.isDesktop) {
            window.addEventListener('scroll', this.listenScrollEvent);
        }
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        if (!this.props.deviceType.isDesktop) {
            window.removeEventListener('scroll', this.listenScrollEvent);
        }
    }

    /* istanbul ignore next */
    onMouseOpenEditorialDropdown = () => {
        this.setState({ controlledIsOpen: true });
    };

    /* istanbul ignore next */
    onMouseLeaveEditorialDropdown = () => {
        this.setState({ controlledIsOpen: false });
    };

    editorialBtnRenderer = (dataItem) => {
        const {
            adobeAudience,
            componentType = '',
            callToActions = [],
            preferences,
            actions,
        } = this.props;
        let ctaLink = get(dataItem, 'link.url', '');
        const { linkText = '', openInNewTab = false } = dataItem;

        ctaLink = adobeAudience && ctaLink ? `${ctaLink}-_-${adobeAudience}` : ctaLink;
        const analyticsTag = get(callToActions[0], 'analyticsTag', '');
        const plccAnalyticsTagName = get(preferences, 'plccAnalyticsTagName', 'PLCC');
        /* istanbul ignore next */
        if (analyticsTag.toUpperCase().includes(plccAnalyticsTagName) && componentType === 'hero') {
            const payloadParams = {
                plccBanner: this.isPLCCBenefitsPage()
                    ? 'static_rewards_apply'
                    : 'static_home_apply',
            };

            actions.plccViewsAnalytics(payloadParams);
        }

        return (
            linkText &&
            ctaLink && (
                <Button
                    buttonType="Secondary"
                    {...(this.isPlccEnableButtonComponent() &&
                        componentType === 'hero' && { onClick: this.applyPlccAnalytics })}
                    className={[cx('editorialCTABtn'), dt(['whitespace-nowrap'])].join(' ')}
                    linkUrl={ctaLink}
                    type="linkbutton"
                    target={openInNewTab ? '_blank' : ''}
                >
                    <RichText text={linkText} />
                </Button>
            )
        );
    };

    getStyleData = () => {
        const { style } = this.props;
        return {
            editorialHeroStyleA: style === 'hero',
            editorialHeroStyleB: style === 'product',
            editorialStyle: style === 'editorial',
        };
    };

    /* istanbul ignore next */
    listenScrollEvent = () => {
        if (this.state.controlledIsOpen) {
            this.setState({ controlledIsOpen: false });
        }
    };

    selectThemes = () => {
        const { theme, deviceType } = this.props;
        const themeName = deviceType.isDesktop ? theme.large : theme.compact;
        return configuredThemes[themeName] || {};
    };

    alignmentThemes = () => {
        const { theme } = this.props;
        const themePosition = theme.alignment;
        return configuredThemes[themePosition] || {};
    };

    parseCTAOptions = (sortOptions) =>
        sortOptions.map((key) => {
            const primaryLinkUrl = get(key, 'link.url', '');
            return {
                displayKey: key.linkText,
                value: primaryLinkUrl,
                openLinkInNewTab: key.openInNewTab,
            };
        });

    navigateToLinks = (event, url, dataItem) => {
        event.preventDefault();
        const openLinkInNewTab = get(dataItem, 'openLinkInNewTab', '');
        /* istanbul ignore next */
        return !__SERVER__ ? NavigationHelper.navigate(url, openLinkInNewTab) : '';
    };

    listItemRenderer = (filteredCallToActions) => {
        const dataSource = this.parseCTAOptions(filteredCallToActions);
        return dataSource.map((dataItem) => (
            <li key={dataItem.displayKey}>
                <Link
                    className={cx('defaultAnchor')}
                    href={dataItem.value}
                    openLinkInNewTab={dataItem.openInNewTab} //eslint-disable-line
                    onClick={(event) => {
                        this.navigateToLinks(event, dataItem.value, dataItem);
                    }} //eslint-disable-line
                >
                    <span //eslint-disable-line
                        data-automation-id="select-options" //eslint-disable-line
                        className={cx('options', 'optionsTheme')} //eslint-disable-line
                    >
                        {dataItem.displayKey}
                    </span>
                </Link>
            </li>
        ));
    };

    /* istanbul ignore next */
    callBackFun = (isOpen) => {
        this.setState({
            fullExclusionDisplay: isOpen,
            controlledIsOpen: isOpen,
        });
    };

    renderImage = () => {
        const {
            adobeAudience,
            callToActions,
            componentType,
            marketingLabel,
            media,
            pageType,
            preferences,
            primaryLink,
            actions,
            onImageLoad,
        } = this.props;
        const { url = '', altText = '', dimensions = {} } = media;

        const { imageError } = this.state;

        const isNucleus = componentType === 'nucleus';

        const firstCTALink = get(callToActions[0], 'link.url', '');

        const primaryLinkUrl = get(primaryLink, 'link.url', '');
        const analyticsTag = get(primaryLink, 'analyticsTag', '');
        const plccAnalyticsTagName = get(preferences, 'plccAnalyticsTagName', 'PLCC');
        if (
            analyticsTag.toUpperCase().includes(plccAnalyticsTagName) &&
            componentType === 'nucleus'
        ) {
            const payloadParams = {
                plccBanner: this.isPLCCBenefitsPage()
                    ? 'static_rewards_apply'
                    : 'static_home_apply',
            };

            actions.plccViewsAnalytics(payloadParams);
        }
        const href = primaryLinkUrl || firstCTALink;
        const openInNewTab = get(primaryLink, 'openInNewTab', '');
        const imgTarget = openInNewTab ? '_blank' : '';
        const imageExt = checkUrlExtension(url);
        const convUrl = replaceSubdomain(url, preferences);
        const plccBenefitPageURL = get(preferences, 'plccBenefitsUrl', '/m/plcc-benefits');
        const isPlccBenefitPage =
            !__SERVER__ && window.location.href.indexOf(plccBenefitPageURL) > -1;
        const componentIsMappedForSTI =
            componentType === 'nucleus' ||
            componentType === 'story' ||
            componentType === 'categorypromo';

        const imageTag =
            imageExt === 'mp4' ? (
                <a href={href && adobeAudience ? `${href}-_-${adobeAudience}` : href}>
                    <video
                        playsInline
                        autoPlay
                        muted
                        loop
                        width="100%"
                        height="100%"
                        className={cx('videoTransition')}
                    >
                        <source
                            src={convUrl}
                            type="video/mp4"
                            automationId="responsive-component-image-video"
                            altText={altText}
                            target={imgTarget}
                            ariaLabel="responsive-component-image"
                        />
                    </video>
                </a>
            ) : (
                <Banner
                    plccName={isPlccBenefitPage ? 'static_rewards_apply' : ''}
                    plccId={isPlccBenefitPage ? 'at-link-plccBanner-id' : ''}
                    adobeAudience={adobeAudience}
                    altText={altText}
                    ariaLabel="responsive-component-image"
                    automationId="responsive-component-image"
                    bannerClass={cx('editorialBannerImageBlock')}
                    bannerImageClass={cx(
                        'imgHoverAnim',
                        'editorialBannerImage',
                        {
                            pdpImageBlock: pageType === 'PDPMARKETING',
                            imageError,
                        },
                        media.className
                    )}
                    bannerImageUrl={convUrl}
                    height={dimensions ? dimensions.height : ''}
                    href={href}
                    onImageError={
                        /* istanbul ignore next */ () => this.setState({ imageError: true })
                    }
                    onImageLoad={(target) => {
                        onImageLoad(target);
                    }}
                    target={imgTarget}
                    width={dimensions ? dimensions.width : ''}
                    imageSTIClassName={componentIsMappedForSTI ? media.className : ''}
                />
            );

        const imgTag = (
            <div className={cx('imageStatusBlock')} data-automation-id="responsive-image-block">
                <div className={cx('imageBlock')}>
                    {isNucleus ? (
                        <RevieveModal
                            data={analyticsTag}
                            isCoremedia={false}
                            componentToBeRendered={imageTag}
                        />
                    ) : (
                        imageTag
                    )}
                </div>
                {/* This will be seperate component in future */}
                {isNucleus && marketingLabel && (
                    <div
                        className={dt([
                            'inline-block',
                            'bottom-0',
                            'left-0',
                            'font-bold',
                            'text-xs',
                            'z-[2]',
                            'bg-gray-60',
                            'bg-opacity-80',
                            'absolute',
                            'text-center',
                            'text-white',
                            'uppercase',
                            'w-full',
                            'p-2',
                        ])}
                    >
                        {marketingLabel}
                    </div>
                )}
                {/* This will be seperate component in future Ends */}
            </div>
        );
        return imgTag;
    };

    renderVideo = () => {
        const { componentType, media, rndKeyString } = this.props;
        return (
            <Video
                isResponsive
                provider={getVideoProvider(media.provider)}
                playerProps={media}
                playerId={rndKeyString}
                componentType={componentType}
                disableAutoPlay
                videoTheme={cx('videoDisplayClass')}
            />
        );
    };

    renderTooltipData = (filteredCallToActions) => (
        <ul>{this.listItemRenderer(filteredCallToActions)}</ul>
    );

    renderEditorialCTA = (btnAutomationId) => {
        const {
            automationId,
            style,
            deviceType,
            callToActions,
            callToActionsGroupLabel,
        } = this.props;
        const editorialDeskTopEventHandler = deviceType.isDesktop
            ? {
                  onMouseOver: this.onMouseOpenEditorialDropdown,
                  onMouseLeave: this.onMouseLeaveEditorialDropdown,
              }
            : {};
        const isProductStyle = style === 'product';
        const editoralBlockStyle =
            callToActions && callToActions.length > 0
                ? cx('editorialBtnStyle', `editorialCtaCount${callToActions.length}`)
                : cx('editorialBtnStyle');
        const filteredCallToActions = callToActions.filter(
            (dataItem) => get(dataItem, 'link.url') && get(dataItem, 'linkText')
        );
        const showDropdown =
            filteredCallToActions.length > 2 &&
            (!isProductStyle || (!deviceType.isDesktop && isProductStyle));
        const shopNowLabel = callToActionsGroupLabel || 'Shop Now';
        const ctaBtnData = showDropdown ? (
            <div
                {...editorialDeskTopEventHandler}
                className={cx('wrapper', 'dropdownWrapper')}
                data-automation-id={automationId}
            >
                <YodaToolTip
                    renderTooltipContents={this.renderTooltipData(filteredCallToActions)}
                    direction={null}
                    controlledIsOpen={this.state.controlledIsOpen}
                    tooltipPlacement="Right"
                    triggerEventName="click"
                    theme={cx('dropdown', 'dropdownTheme')}
                    callBackFun={this.callBackFun}
                    tooltipTextClass={cx('tooltipClass')}
                    tooltipBodyClassName={cx('editorialTooltip')}
                >
                    <div>
                        <span className={cx('defaultDisplay', 'buttonTitle')}>{shopNowLabel}</span>
                        <Icon
                            iconType="svg"
                            className={cx('icon', 'iconClass')}
                            viewBox="0 0 24 24"
                            width="36px"
                            height="36px"
                            automationId="collapse-right"
                            pathClassName={cx('rightIcon')}
                        >
                            <IconTriangleDown />
                        </Icon>
                    </div>
                </YodaToolTip>
            </div>
        ) : (
            <List
                listStyleClass={editoralBlockStyle}
                spacing="None"
                listBodyClass={cx('editorialBtnBody')}
                itemStyleClass={cx('editorialBtnItem')}
                datasource={filteredCallToActions}
                direction="Wrap"
                childRenderer={this.editorialBtnRenderer}
                automationId={btnAutomationId}
            />
        );
        return ctaBtnData;
    };

    renderContent = () => {
        const {
            adobeAudience,
            automationId,
            componentType,
            ctaPosition,
            deviceType,
            headlineAbove,
            logo,
            media,
            numberOfColumns,
            preferences,
            style,
            transparent,
        } = this.props;
        const { imageError } = this.state;
        const { url = '', altText = '' } = logo;
        const isNucleus = componentType === 'nucleus';
        let isRenderEditorialCTA = true;
        /* istanbul ignore next */
        if (
            componentType === 'editorialDealStyleA' ||
            componentType === 'editorialDealStyleA6cat' ||
            componentType === 'editorialDealStyleA8cat' ||
            componentType === 'editorialDealStyleB' ||
            componentType === 'editorialDealStyleC'
        ) {
            if (deviceType.isMobile) {
                isRenderEditorialCTA = false;
            }
            if (ctaPosition === 'cta-middle' && deviceType.isMobile) {
                isRenderEditorialCTA = true;
            }
        }
        const { fullExclusionDisplay } = this.state;
        const editorialDropDown = fullExclusionDisplay ? 'editorialDropDown' : '';

        const mediaType = get(media, 'assetType', '');
        let mediaTag = '';
        if (mediaType === 'image') {
            mediaTag = this.renderImage();
        } else if (mediaType === 'video') {
            mediaTag = this.renderVideo();
        }

        const {
            mediaAlign: imageAlignment = '',
            textAlign: textAlignment = '',
        } = this.selectThemes();
        const { alignTheme: alignmentTheme = '' } = this.alignmentThemes();
        const alignmentBlockStyle = alignmentTheme || '';
        const textBlockStyle = textAlignment ? `text${textAlignment}` : '';
        const imageRight = imageAlignment === 'right';
        const { editorialHeroStyleB, editorialStyle } = this.getStyleData();

        /*  ignore applying bg color/brand logo to  the story style product/editorial */
        const isIgnoreBgColorLogo = editorialHeroStyleB || editorialStyle;
        /* istanbul ignore next */
        const imageExt = checkUrlExtension(url);
        const convUrl = replaceSubdomain(url, preferences);

        const logoBlock =
            (url || altText) && style && !isIgnoreBgColorLogo ? (
                <div className={cx('logoBlock')}>
                    {imageExt === 'mp4' ? (
                        <video
                            playsInline
                            autoPlay
                            muted
                            loop
                            width="100%"
                            height="100%"
                            className={cx('videoTransition')}
                        >
                            <source
                                src={convUrl}
                                type="video/mp4"
                                automationId="logo-image"
                                onImageError={
                                    /* istanbul ignore next */ () =>
                                        this.setState({ imageError: true })
                                }
                                altText={altText}
                                ariaLabel="logo-image"
                            />
                        </video>
                    ) : (
                        <Banner
                            adobeAudience={adobeAudience}
                            altText={altText}
                            ariaLabel="logo-image"
                            automationId="logo-image"
                            bannerImageClass={cx({ imageError })}
                            bannerImageUrl={replaceSubdomain(url, preferences)}
                            onImageError={() => this.setState({ imageError: true })}
                        />
                    )}
                </div>
            ) : (
                ''
            );

        const hideBgColor = headlineAbove ? 'hideBgColor' : '';
        /* istanbul ignore next */
        const transparentClass =
            componentType === 'nucleus' && !transparent ? 'showTransparent' : '';
        const noPosition =
            componentType === 'herovariant' && (!media || !media.url) ? 'noPosition' : '';
        const textTag = (
            <div
                className={cx(
                    'contentBlock',
                    editorialDropDown,
                    textBlockStyle,
                    alignmentBlockStyle,
                    `responsiveCol${numberOfColumns}`,
                    hideBgColor,
                    noPosition,
                    transparentClass
                )}
                data-automation-id="responsive-content-block"
            >
                <div className={cx('contentInfoBlock')}>
                    {!headlineAbove && <HeadingComponent {...this.props} />}
                    {isRenderEditorialCTA && (
                        <div className={cx('linkBtnBlock')}>
                            {/* Button Block in Future it will be seperate component */}
                            {!isNucleus && (
                                <div className={cx('editorialBtnBlock')}>
                                    {this.renderEditorialCTA(automationId)}
                                </div>
                            )}
                            {/* Button Block in Future it will be seperate component Ends */}
                        </div>
                    )}
                </div>
                {logoBlock}
            </div>
        );
        /* istanbul ignore next */
        return imageRight ? (
            <>
                {textTag}
                {mediaTag}
            </>
        ) : (
            <>
                {mediaTag}
                {textTag}
            </>
        );
    };

    /* istanbul ignore next */
    isPLCCBenefitsPage = () => {
        const { preferences } = this.props;
        const plccBenefitPageURL = get(preferences, 'plccBenefitsUrl', '/m/plcc-benefits');
        return !__SERVER__ && window.location.href.indexOf(plccBenefitPageURL) > -1;
    };

    /* istanbul ignore next */
    applyPlccAnalytics = () => {
        const { actions } = this.props;
        const payloadParams = {
            plccBanner: this.isPLCCBenefitsPage() ? 'static_rewards_apply' : 'static_home_apply',
        };

        actions.plccApplyAnalytics(payloadParams);
    };

    /* istanbul ignore next */
    isPlccEnableButtonComponent = () => {
        const { preferences } = this.props;
        const analyticsTag = get(this.props, 'callToActions[0].analyticsTag', '');
        const plccAnalyticsTagName = get(preferences, 'plccAnalyticsTagName', 'PLCC');
        return analyticsTag.toUpperCase().includes(plccAnalyticsTagName);
    };

    /* istanbul ignore next */
    isPlccEnableLink = () => {
        const { primaryLink, preferences } = this.props;
        const analyticsTag = get(primaryLink, 'analyticsTag', '');
        const plccAnalyticsTagName = get(preferences, 'plccAnalyticsTagName', 'PLCC');
        return analyticsTag.toUpperCase().includes(plccAnalyticsTagName);
    };

    render() {
        const { automationId, componentType, style, imageWidth, backgroundColor } = this.props;
        const { mediaAlign: imageAlignment = '' } = this.selectThemes();
        const { alignTheme: alignmentTheme = '' } = this.alignmentThemes();
        const imageTopAlignment = imageAlignment === 'center';
        const textAlignment = alignmentTheme === 'left-right';
        const imageBlockStyle = imageAlignment ? `image${imageAlignment}` : '';
        const editorialBgColor = {};

        const { editorialHeroStyleA, editorialHeroStyleB, editorialStyle } = this.getStyleData();

        /*  ignore applying bg color/brand logo to  the story style product/editorial */
        const isIgnoreBgColorLogo = editorialHeroStyleB || editorialStyle;
        const styleBasedBackGroundColor = !isIgnoreBgColorLogo ? backgroundColor : '';
        editorialBgColor.background = style ? styleBasedBackGroundColor : backgroundColor;
        const backgroundColorClass = backgroundColor ? 'backgroundColorClass' : '';
        const styletype = style
            ? `responsive-${componentType}-${style}`
            : `responsive-${componentType}`;
        // let fullWidth;
        // if (window.yodairis) {
        //     fullWidth = (fullBleed) && 'fullWidth';
        // }
        return (
            <div
                data-automation-id={styletype}
                {...(this.isPlccEnableLink() &&
                    componentType === 'nucleus' && { onClick: this.applyPlccAnalytics })}
                className={cx('editorialContentContainer', componentType, style)}
            >
                <div
                    className={cx(
                        'editorialNucleusContent',
                        backgroundColorClass,
                        componentType,
                        imageWidth,
                        style,
                        { editorialHeroStyleA },
                        { editorialHeroStyleB },
                        imageBlockStyle,
                        { imageTop: imageTopAlignment },
                        textAlignment
                    )}
                    data-automation-id={automationId}
                    style={editorialBgColor}
                >
                    {this.renderContent()}
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            ...analyticsAction,
        },
        dispatch
    ),
});

export default connect(null, mapDispatchToProps)(EditorialContent);
